import React, { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPut } from '../../lib/api';
import { ParamDataType, ParamCategory } from '@bisa/shared';
import { Button } from '../../components/ui/Button';
import { Input, Select } from '../../components/ui/Input';
import { PageSpinner } from '../../components/ui/Spinner';

interface GlobalParam {
  id: string;
  paramKey: string;
  paramValue: string;
  dataType: ParamDataType;
  description: string;
  category: ParamCategory;
  isEditable: boolean;
}

function ParamInput({
  param,
  onSave,
}: {
  param: GlobalParam;
  onSave: (key: string, value: string) => Promise<void>;
}) {
  const [value, setValue] = useState(param.paramValue);
  const [saving, setSaving] = useState(false);
  const dirty = value !== param.paramValue;

  const save = async () => {
    setSaving(true);
    await onSave(param.paramKey, value);
    setSaving(false);
  };

  return (
    <tr className="align-top">
      <td className="px-4 py-3">
        <p className="text-sm font-mono font-medium text-gray-900">{param.paramKey}</p>
        <p className="text-xs text-gray-400">{param.description}</p>
      </td>
      <td className="px-4 py-3">
        <span className="text-xs text-gray-500">{param.category}</span>
      </td>
      <td className="px-4 py-3 w-56">
        {param.dataType === ParamDataType.BOOLEAN ? (
          <Select
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={!param.isEditable}
          >
            <option value="true">true</option>
            <option value="false">false</option>
          </Select>
        ) : (
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={!param.isEditable}
            type={param.dataType === ParamDataType.INTEGER || param.dataType === ParamDataType.DECIMAL ? 'number' : 'text'}
          />
        )}
      </td>
      <td className="px-4 py-3">
        {param.isEditable && dirty && (
          <Button size="sm" loading={saving} onClick={save}>Save</Button>
        )}
        {!param.isEditable && (
          <span className="text-xs text-gray-400">System</span>
        )}
      </td>
    </tr>
  );
}

export function GlobalParamsPage() {
  const [params, setParams] = useState<GlobalParam[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<ParamCategory | ''>('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiGet<GlobalParam[]>('/admin/params/global');
      setParams(data);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (key: string, value: string) => {
    await apiPut('/admin/params/global', { paramKey: key, paramValue: value });
    setParams((prev) =>
      prev.map((p) => (p.paramKey === key ? { ...p, paramValue: value } : p)),
    );
  };

  const visible = filter ? params.filter((p) => p.category === filter) : params;

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Global Params</h1>
        <Select value={filter} onChange={(e) => setFilter(e.target.value as ParamCategory | '')} className="w-44">
          <option value="">All categories</option>
          {Object.values(ParamCategory).map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </Select>
      </div>

      {loading && <PageSpinner />}
      {error && <p className="text-red-600">{error}</p>}

      {!loading && !error && (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Key / Description', 'Category', 'Value', ''].map((h, i) => (
                  <th key={i} className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {visible.map((p) => (
                <ParamInput key={p.id} param={p} onSave={handleSave} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
